import { parseDate } from './conversions/convertDateString';

/**
 * Represents a single parsed entry from the lastOrigin data.
 */
export interface LastOriginEntry {
  /** Category of the entry (e.g., "Country", "PSS", "Region", "Source tag") */
  category: string;
  /** The name/value (e.g., "Denmark", "002190067", "rORBCOMM000") */
  name: string;
  /** Source type (e.g., "AIS", "DAS") */
  sourceType: string;
  /** Parsed timestamp as Date object */
  timestamp: Date | null;
  /** Original timestamp string for debugging */
  timestampRaw: string;
  /** Latitude coordinate */
  latitude: number | null;
  /** Longitude coordinate */
  longitude: number | null;
}

/**
 * Result of parsing lastOrigin data.
 */
export interface ParsedLastOrigin {
  /** All parsed entries as a flat list */
  entries: LastOriginEntry[];
  /** Total count of entries */
  totalCount: number;
  /** Whether parsing was successful */
  isValid: boolean;
}

/**
 * Parses a single entry string in the format: "Name/SourceType/Timestamp/lat,lon"
 * Example: "Denmark/AIS/27 Jan 2026 11:04:07 UTC/54.517112,7.761940"
 */
function parseEntryString(
  entryString: string,
  category: string,
): LastOriginEntry | null {
  if (!entryString || typeof entryString !== 'string') {
    return null;
  }

  // Split by "/" but we need to be careful - timestamp contains spaces, not slashes
  // Format: Name/SourceType/Timestamp/lat,lon
  // The timestamp part is "27 Jan 2026 11:04:07 UTC" (no slashes)
  // So we can split by "/" and expect 4 parts
  const parts = entryString.split('/');

  if (parts.length < 4) {
    // Malformed entry - return with what we have
    return {
      category,
      name: parts[0] || '',
      sourceType: parts[1] || '',
      timestampRaw: parts[2] || '',
      timestamp: parts[2] ? parseDate(parts[2]) : null,
      latitude: null,
      longitude: null,
    };
  }

  const name = parts[0];
  const sourceType = parts[1];
  const timestampRaw = parts[2];
  const coordsString = parts[3];

  // Parse coordinates (format: "lat,lon")
  let latitude: number | null = null;
  let longitude: number | null = null;

  if (coordsString && coordsString.includes(',')) {
    const [latStr, lonStr] = coordsString.split(',');
    const parsedLat = parseFloat(latStr);
    const parsedLon = parseFloat(lonStr);

    if (!isNaN(parsedLat)) {
      latitude = parsedLat;
    }
    if (!isNaN(parsedLon)) {
      longitude = parsedLon;
    }
  }

  // Parse timestamp using existing parseDate function
  const timestamp = parseDate(timestampRaw);

  return {
    category,
    name,
    sourceType,
    timestampRaw,
    timestamp,
    latitude,
    longitude,
  };
}

/**
 * Parses the lastOrigin JSON string into a structured format.
 *
 * Input format:
 * ```json
 * {
 *   "Country": ["Denmark/AIS/27 Jan 2026 11:04:07 UTC/54.517112,7.761940"],
 *   "PSS": ["002190067/AIS/27 Jan 2026 11:04:07 UTC/54.517112,7.761940"],
 *   "Source tag": ["rORBCOMM000/AIS/27 Jan 2026 10:56:59 UTC/54.517117,7.761952", ...]
 * }
 * ```
 *
 * @param lastOrigin - The raw lastOrigin string from the API
 * @returns Parsed data with entries as a flat list
 */
export function parseLastOrigin(
  lastOrigin: string | undefined | null,
): ParsedLastOrigin {
  // Handle empty/null/undefined cases
  if (!lastOrigin || lastOrigin === 'null' || lastOrigin.trim() === '') {
    return {
      entries: [],
      totalCount: 0,
      isValid: false,
    };
  }

  try {
    // Parse the JSON string
    const data = JSON.parse(lastOrigin);

    // Handle case where parsed result is null
    if (data === null) {
      return {
        entries: [],
        totalCount: 0,
        isValid: false,
      };
    }

    // Handle case where it's not an object
    if (typeof data !== 'object' || Array.isArray(data)) {
      return {
        entries: [],
        totalCount: 0,
        isValid: false,
      };
    }

    const entries: LastOriginEntry[] = [];

    // Iterate through each category (Country, PSS, Region, Source tag, etc.)
    for (const [category, entryArray] of Object.entries(data)) {
      if (!Array.isArray(entryArray)) {
        continue;
      }

      for (const entryString of entryArray) {
        const parsed = parseEntryString(entryString, category);
        if (parsed) {
          entries.push(parsed);
        }
      }
    }

    return {
      entries,
      totalCount: entries.length,
      isValid: entries.length > 0,
    };
  } catch (error) {
    // JSON parsing failed
    console.warn('Failed to parse lastOrigin:', error);
    return {
      entries: [],
      totalCount: 0,
      isValid: false,
    };
  }
}
